import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  ChevronDownIcon,
  ChevronRightIcon,
  FireIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';

interface Workout {
  id: string;
  name: string;
  description?: string;
  status: string;
  scheduledDate?: string;
  estimatedDuration?: number;
  completedDate?: string;
  overallFeedback?: string;
  overallRating?: number;
  createdAt: string;
}

type AccordionType = 'scheduled' | 'completed' | null;

const ClientWorkouts: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [openAccordion, setOpenAccordion] = useState<AccordionType>('scheduled');

  // No date filtering needed anymore

  // Fetch workouts
  const { data: workoutsData, isLoading, error } = useQuery({
    queryKey: ['client-workouts'],
    queryFn: () => api.get('/workouts'),
    enabled: !!user,
  });

  // Handle both response formats - new API returns { workouts: [] }, old API returns workouts directly
  const allWorkouts = workoutsData?.data?.workouts || workoutsData?.data || [];
  


  // Filter workouts - simple logic
  const scheduledWorkouts = allWorkouts.filter((workout: Workout) => {
    return workout.status !== 'COMPLETED' && !workout.completedDate;
  });

  const completedWorkouts = allWorkouts.filter((workout: Workout) => {
    return workout.status === 'COMPLETED' || workout.completedDate;
  });


  


  const toggleAccordion = (accordion: AccordionType) => {
    setOpenAccordion(openAccordion === accordion ? null : accordion);
  };

  const formatDate = (dateString: string) => {
    // Handle date strings that might be in YYYY-MM-DD format or ISO format
    let date: Date;
    if (dateString.includes('T')) {
      // ISO format - extract just the date part to avoid timezone issues
      const datePart = dateString.split('T')[0];
      date = new Date(datePart + 'T00:00:00');
    } else {
      // Already in YYYY-MM-DD format
      date = new Date(dateString + 'T00:00:00');
    }
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };


  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading workouts...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Error loading workouts</p>
          <p className="text-gray-500 text-sm mt-2">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 -mt-6">
      {/* Sticky Header */}
      <div className="sticky top-16 z-20 bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center space-x-2">
          <FireIcon className="h-5 w-5 text-blue-600" />
          <div>
            <h1 className="text-lg font-semibold text-gray-900">
              Your Workouts
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-8 space-y-4">
        
        {/* This Week's Workouts Accordion */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <button
            onClick={() => toggleAccordion('scheduled')}
            className="w-full px-3 py-3 text-left hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <h3 className="text-base font-medium text-gray-900">Scheduled Workouts</h3>
                <span className="bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full text-xs font-medium">
                  {scheduledWorkouts.length}
                </span>
              </div>
              {openAccordion === 'scheduled' ? (
                <ChevronDownIcon className="h-4 w-4 text-gray-400" />
              ) : (
                <ChevronRightIcon className="h-4 w-4 text-gray-400" />
              )}
            </div>
          </button>
          
          {openAccordion === 'scheduled' && (
            <div className="border-t border-gray-200">
              {scheduledWorkouts.length === 0 ? (
                <div className="p-4 text-center">
                  <FireIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                  <h3 className="text-sm font-medium text-gray-900">No scheduled workouts</h3>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {scheduledWorkouts
                    .sort((a: Workout, b: Workout) => {
                      const aDate = new Date(a.scheduledDate || a.createdAt);
                      const bDate = new Date(b.scheduledDate || b.createdAt);
                      return bDate.getTime() - aDate.getTime();
                    })
                    .map((workout: Workout) => (
                      <div 
                        key={workout.id} 
                        className="p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/client/workouts/${workout.id}`)}
                      >
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-gray-900 text-sm">{workout.name}</h4>
                          
                          <span className="text-xs text-gray-500">
                            {workout.status === 'COMPLETED' && workout.completedDate 
                              ? formatDate(workout.completedDate)
                              : formatDate(workout.scheduledDate || workout.createdAt)
                            }
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Completed Workouts Accordion */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <button
            onClick={() => toggleAccordion('completed')}
            className="w-full px-3 py-3 text-left hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <h3 className="text-base font-medium text-gray-900">Completed Workouts</h3>
                <span className="bg-green-100 text-green-600 px-2 py-0.5 rounded-full text-xs font-medium">
                  {completedWorkouts.length}
                </span>
              </div>
              {openAccordion === 'completed' ? (
                <ChevronDownIcon className="h-4 w-4 text-gray-400" />
              ) : (
                <ChevronRightIcon className="h-4 w-4 text-gray-400" />
              )}
            </div>
          </button>
          
          {openAccordion === 'completed' && (
            <div className="border-t border-gray-200">
              {completedWorkouts.length === 0 ? (
                <div className="p-4 text-center">
                  <CheckCircleIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                  <h3 className="text-sm font-medium text-gray-900">No completed workouts</h3>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {completedWorkouts
                    .sort((a: Workout, b: Workout) => {
                      const aDate = new Date(a.completedDate || a.createdAt);
                      const bDate = new Date(b.completedDate || b.createdAt);
                      return bDate.getTime() - aDate.getTime();
                    })
                    .map((workout: Workout) => (
                      <div 
                        key={workout.id} 
                        className="p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/client/workouts/${workout.id}`)}
                      >
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-gray-900 text-sm">{workout.name}</h4>
                          
                          <span className="text-xs text-gray-500">
                            {formatDate(workout.completedDate || workout.createdAt)}
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientWorkouts;
